<template>
  <div class="his-fee-container">
    <!-- 视图模式切换和操作栏 -->
    <el-row class="toolbar margin-bottom">
      <el-col :span="12">
        <el-radio-group v-model="viewMode" size="small" @change="handleViewModeChange">
          <el-radio-button label="merged">
            <i class="el-icon-s-fold"></i> 合并显示
          </el-radio-button>
          <el-radio-button label="detail">
            <i class="el-icon-s-unfold"></i> 明细显示
          </el-radio-button>
        </el-radio-group>
        <el-tooltip v-if="viewMode === 'merged'" content="同一批次缴费的同类费用将合并为一条记录展示" placement="top">
          <i class="el-icon-question text-primary margin-left-sm"></i>
        </el-tooltip>
      </el-col>
      <el-col :span="12" style="text-align: right;">
        <el-button v-if="showDetailDialog" size="small" @click="showDetailDialog = false">
          <i class="el-icon-back"></i> 返回合并列表
        </el-button>
      </el-col>
    </el-row>

    <!-- 批量操作栏 -->
    <el-row v-if="selectedItems.length > 0" class="batch-toolbar margin-bottom" style="margin-top: 15px;">
      <el-col :span="24">
        <el-alert
          :title="`已选择 ${selectedItems.length} 条记录，合计实付金额 ${selectedTotalAmount} 元`"
          type="info"
          :closable="false"
          show-icon
        >
          <template slot="default">
            <el-button
              type="primary"
              size="mini"
              style="margin-left: 20px;"
              @click="openBatchReturnPayFeeDialog"
            >
              批量申请退费
            </el-button>
            <el-button size="mini" @click="clearSelection">取消选择</el-button>
          </template>
        </el-alert>
      </el-col>
    </el-row>

    <!-- 合并/明细列表 -->
    <el-table
      v-if="!showDetailDialog"
      ref="feeTable"
      :data="feeDetails"
      border
      row-key="detailId"
      @selection-change="handleSelectionChange"
    >
      <el-table-column type="selection" width="55" :selectable="isSelectable" reserve-selection />
      <el-table-column prop="feeName" :label="$t('adminRoomFee.feeName')" align="center" />
      <el-table-column prop="payerObjName" :label="$t('adminRoomFee.payerObj')" align="center" />
      <el-table-column :label="$t('adminRoomFee.cycles')" align="center" width="120">
        <template slot-scope="scope">
          <span v-if="scope.row.detailIds && scope.row.detailIds.length > 0">
            <el-tag size="mini" type="success">{{ scope.row.detailIds.split(',').length }}个月</el-tag>
          </span>
          <span v-else>{{ scope.row.cycles }}</span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('adminRoomFee.amount')" align="center" width="180">
        <template slot-scope="scope">
          <div>应收: {{ scope.row.receivableAmount }}</div>
          <div>实收: {{ scope.row.receivedAmount }}</div>
          <div v-if="scope.row.acctAmount > 0">
            {{ $t('adminRoomFee.accountDeduction') }}: {{ scope.row.acctAmount }}
          </div>
          <div v-for="(item, index) in scope.row.payFeeDetailDiscountDtoList" :key="index">
            {{ item.discountName }}: {{ Math.abs(item.discountPrice) }}
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="primeRateName" :label="$t('adminRoomFee.paymentMethod')" align="center" />
      <el-table-column :label="$t('adminRoomFee.paymentPeriod')" align="center" width="180">
        <template slot-scope="scope">
          {{ dfTime(scope.row.startTime) }}~<br>
          {{ dfTime(scope.row.endTime) }}
        </template>
      </el-table-column>
      <el-table-column prop="createTime" :label="$t('adminRoomFee.paymentTime')" align="center" />
      <el-table-column prop="cashierName" :label="$t('adminRoomFee.cashier')" align="center" />
      <el-table-column prop="stateName" :label="$t('adminRoomFee.status')" align="center" />
      <el-table-column prop="remark" :label="$t('adminRoomFee.remark')" align="center" />
      <el-table-column label="操作" align="center" width="120">
        <template slot-scope="scope">
          <el-button
            v-if="scope.row.detailIds && scope.row.detailIds.length > 0"
            size="mini"
            type="primary"
            @click="viewDetail(scope.row.detailIds)"
          >
            查看明细
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 明细弹窗列表（点击"查看明细"后展示） -->
    <el-table
      v-else
      ref="feeTable"
      :data="detailFeeDetails"
      border
      row-key="detailId"
      @selection-change="handleSelectionChange"
    >
      <el-table-column type="selection" width="55" :selectable="isSelectable" reserve-selection />
      <el-table-column prop="feeName" :label="$t('adminRoomFee.feeName')" align="center" />
      <el-table-column prop="cycles" :label="$t('adminRoomFee.cycles')" align="center" />
      <el-table-column :label="$t('adminRoomFee.amount')" align="center" width="180">
        <template slot-scope="scope">
          <div>应收: {{ scope.row.receivableAmount }}</div>
          <div>实收: {{ scope.row.receivedAmount }}</div>
          <div v-if="scope.row.acctAmount > 0">
            {{ $t('adminRoomFee.accountDeduction') }}: {{ scope.row.acctAmount }}
          </div>
          <div v-for="(item, index) in scope.row.payFeeDetailDiscountDtoList" :key="index">
            {{ item.discountName }}: {{ Math.abs(item.discountPrice) }}
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="primeRateName" :label="$t('adminRoomFee.paymentMethod')" align="center" />
      <el-table-column :label="$t('adminRoomFee.paymentPeriod')" align="center" width="180">
        <template slot-scope="scope">
          {{ dfTime(scope.row.startTime) }}~<br>
          {{ dfTime(scope.row.endTime) }}
        </template>
      </el-table-column>
      <el-table-column prop="createTime" :label="$t('adminRoomFee.paymentTime')" align="center" />
      <el-table-column prop="cashierName" :label="$t('adminRoomFee.cashier')" align="center" />
      <el-table-column prop="stateName" :label="$t('adminRoomFee.status')" align="center" />
      <el-table-column prop="remark" :label="$t('adminRoomFee.remark')" align="center" />
    </el-table>

    <el-pagination class="margin-top" :current-page="page.current" :page-size="page.size" :total="page.total"
      layout="total, sizes, prev, pager, next, jumper" @size-change="handleSizeChange"
      @current-change="handleCurrentChange" />

    <!-- 批量退费弹窗 -->
    <batch-return-pay-fee-dialog
      ref="batchReturnPayFeeDialog"
      @confirm="handleBatchReturnPayFeeConfirm"
    />

    <!-- 批量退费进度弹窗 -->
    <batch-return-pay-fee-progress
      ref="batchReturnPayFeeProgress"
      @complete="handleBatchReturnPayFeeComplete"
    />
  </div>
</template>

<script>
/**
 * 缴费历史组件 - 支持合并/明细切换展示
 *
 * 功能说明：
 * 1. 合并模式：将同一批次（相同payOrderId）的同类费用（相同configId）合并为一条记录展示
 * 2. 明细模式：按原始记录逐条展示
 * 3. 合并记录支持"查看明细"功能，可展开查看该合并记录包含的原始明细
 *
 * 开发时间：2024-06-01
 * 修改内容：
 * - 新增 viewMode 视图模式切换（合并/明细）
 * - 新增 queryMergedFeeDetail API 调用
 * - 新增 detailIds 字段处理，支持查看合并明细
 * - 新增 showDetailDialog 状态，支持在合并列表中查看明细
 */
import { queryAdminFeeDetail } from '@/api/fee/adminRoomFeeApi'
import { queryMergedFeeDetail } from '@/api/fee/adminRoomFeeApi'
import { dateFormat } from '@/utils/dateUtil'
import BatchReturnPayFeeDialog from './batchReturnPayFeeDialog'
import BatchReturnPayFeeProgress from './batchReturnPayFeeProgress'

export default {
  name: 'ARoomDetailHisFee',

  components: {
    BatchReturnPayFeeDialog,
    BatchReturnPayFeeProgress
  },

  data() {
    return {
      feeDetails: [],
      detailFeeDetails: [], // 查看明细时的临时数据
      showDetailDialog: false, // 是否显示明细弹窗列表
      viewMode: 'merged', // 视图模式：merged(合并) / detail(明细)
      ownerId: '',
      feeId: '',
      selectedItems: [],       // 当前选中的记录
      selectedTotalAmount: 0,   // 选中记录的合计实付金额
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },

  methods: {
    open(params) {
      this.ownerId = params.ownerId
      this.feeId = params.feeId
      this.showDetailDialog = false
      this.loadData()
    },

    /**
     * 加载数据
     * 根据当前 viewMode 选择调用合并查询或明细查询接口
     */
    async loadData() {
      const params = {
        page: this.page.current,
        row: this.page.size,
        ownerId: this.ownerId,
        feeId: this.feeId
      }

      try {
        let res
        if (this.viewMode === 'merged') {
          // 合并模式：调用新增的统一批次合并查询接口
          res = await queryMergedFeeDetail(params)
        } else {
          // 明细模式：调用原有的明细查询接口
          res = await queryAdminFeeDetail(params)
        }
        this.feeDetails = res.feeDetails || []
        this.page.total = res.total || 0
      } catch (error) {
        console.error('查询缴费历史失败:', error)
        this.$message.error('查询缴费历史失败')
      }
    },

    /**
     * 视图模式切换
     */
    handleViewModeChange() {
      this.page.current = 1
      this.showDetailDialog = false
      this.clearSelection()
      this.loadData()
    },

    /**
     * 查看合并记录的明细
     * @param detailIds 逗号分隔的明细ID列表
     */
    async viewDetail(detailIds) {
      if (!detailIds) {
        return
      }
      const ids = detailIds.split(',')
      if (ids.length === 0) {
        return
      }

      this.$message.info(`正在加载 ${ids.length} 条明细记录...`)

      try {
        // 调用原有的明细查询接口，传入 detailIds 查询指定记录
        const params = {
          page: 1,
          row: 50,
          detailIds: ids,
          ownerId: this.ownerId
        }
        const res = await queryAdminFeeDetail(params)
        this.detailFeeDetails = res.feeDetails || []
        this.clearSelection()
        this.showDetailDialog = true
      } catch (error) {
        console.error('查询明细失败:', error)
        this.$message.error('查询明细失败')
      }
    },

    handleSizeChange(val) {
      this.page.size = val
      this.loadData()
    },

    handleCurrentChange(val) {
      this.page.current = val
      this.loadData()
    },

    dfTime(time) {
      return dateFormat(time)
    },

    /**
     * 判断记录是否可选择（用于批量退费）
     * 只有状态为 1400（正常/已缴费）的记录才允许选择
     */
    isSelectable(row) {
      // 正常已缴费状态才可选
      return row.state === '1400'
    },

    /**
     * 选择项变化时触发
     */
    handleSelectionChange(selection) {
      this.selectedItems = selection
      // 计算合计实付金额
      let total = 0
      selection.forEach(item => {
        const amount = parseFloat(item.receivedAmount) || 0
        total += amount
      })
      this.selectedTotalAmount = total.toFixed(2)
    },

    /**
     * 打开批量退费弹窗
     */
    openBatchReturnPayFeeDialog() {
      if (this.selectedItems.length === 0) {
        this.$message.warning('请先选择要退费的记录')
        return
      }
      this.$refs.batchReturnPayFeeDialog.open(this.selectedItems)
    },

    /**
     * 清空选择
     */
    clearSelection() {
      this.selectedItems = []
      this.selectedTotalAmount = 0
      // 清空表格选择状态
      const tableRef = this.$refs.feeTable
      if (tableRef) {
        tableRef.clearSelection()
      }
    },

    /**
     * 批量退费弹窗确认
     */
    handleBatchReturnPayFeeConfirm({ items, reason }) {
      this.$refs.batchReturnPayFeeProgress.open(items, reason)
    },

    /**
     * 批量退费进度完成
     */
    handleBatchReturnPayFeeComplete({ successCount, failCount }) {
      // 清空选择
      this.clearSelection()
      // 刷新列表
      this.loadData()

      if (failCount === 0) {
        this.$message.success(`批量申请退费成功，共 ${successCount} 条记录已提交审核`)
      } else {
        this.$message.warning(`批量申请退费完成，成功 ${successCount} 条，失败 ${failCount} 条`)
      }
    }
  }
}
</script>

<style scoped>
.his-fee-container {
  padding: 10px 0;
}
.toolbar {
  margin-bottom: 15px;
}
.text-primary {
  color: #409EFF;
}
.margin-left-sm {
  margin-left: 8px;
}
.margin-bottom {
  margin-bottom: 10px;
}
.batch-toolbar {
  margin-bottom: 10px;
}
</style>
